import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Joyride from 'react-joyride';
import { getDashboardTourSteps } from '../tours/dashboardTour.jsx';
import { getIncomeManagementTourSteps } from '../tours/incomeManagementTour.jsx';
import { getExpenseTrackingTourSteps } from '../tours/expenseTrackingTour.jsx';
import { getAssetPortfolioTourSteps } from '../tours/assetPortfolioTour.jsx';
import { getDebtLiabilitiesTourSteps } from '../tours/debtLiabilitiesTour.jsx';
import { getSetupTourSteps } from '../tours/setupTour.jsx';
import { getMonteCarloTourSteps } from '../tours/monteCarloTour.jsx';
import { getInvestmentsTourSteps } from '../tours/investmentsTour.jsx';

const TourContext = createContext(null);

const TOUR_STORAGE_KEY = 'retirementpro_tours_seen';

// Helper to check if a tour has been seen
const hasTourBeenSeen = (tourName) => {
  try {
    const seen = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    return seen[tourName] === true;
  } catch {
    return false;
  }
};

// Helper to mark a tour as seen
const markTourAsSeen = (tourName) => {
  try {
    const seen = JSON.parse(localStorage.getItem(TOUR_STORAGE_KEY) || '{}');
    seen[tourName] = true;
    localStorage.setItem(TOUR_STORAGE_KEY, JSON.stringify(seen));
  } catch (e) {
    console.error('Failed to save tour state:', e);
  }
};

export const useTour = () => {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour must be used within TourProvider');
  return ctx;
};

export const TourProvider = ({ children }) => {
  const [run, setRun] = useState(false);
  const [steps, setSteps] = useState([]);
  const [stepIndex, setStepIndex] = useState(0);
  const [currentTourName, setCurrentTourName] = useState(null);

  const startTour = useCallback((tourSteps, tourName) => {
    setSteps(tourSteps);
    setStepIndex(0);
    setCurrentTourName(tourName);
    setRun(true);
  }, []);

  const startDashboardTour = useCallback(() => {
    startTour(getDashboardTourSteps(), 'dashboard');
  }, [startTour]);

  const startIncomeManagementTour = useCallback(() => {
    startTour(getIncomeManagementTourSteps(), 'income-management');
  }, [startTour]);

  const startExpenseTrackingTour = useCallback(() => {
    startTour(getExpenseTrackingTourSteps(), 'expense-tracking');
  }, [startTour]);

  const startAssetPortfolioTour = useCallback(() => {
    startTour(getAssetPortfolioTourSteps(), 'asset-portfolio');
  }, [startTour]);

  const startDebtLiabilitiesTour = useCallback(() => {
    startTour(getDebtLiabilitiesTourSteps(), 'debt-liabilities');
  }, [startTour]);

  const startSetupTour = useCallback(() => {
    startTour(getSetupTourSteps(), 'setup');
  }, [startTour]);

  const startMonteCarloTour = useCallback(() => {
    startTour(getMonteCarloTourSteps(), 'monte-carlo');
  }, [startTour]);

  const startInvestmentsTour = useCallback(() => {
    startTour(getInvestmentsTourSteps(), 'investments-allocations');
  }, [startTour]);

  // Auto-start tour for first-time visitors
  const autoStartTour = useCallback((tourName, startFunction) => {
    if (!hasTourBeenSeen(tourName)) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        startFunction();
      }, 500);
    }
  }, []);

  const handleCallback = useCallback((data) => {
    const { action, index, status, type, step } = data;
    
    // Custom scroll behavior - only scroll if element is not visible
    if (type === 'step:before' && step?.target) {
      const target = document.querySelector(step.target);
      if (target) {
        const rect = target.getBoundingClientRect();
        const isVisible = (
          rect.top >= 80 && // Account for header height
          rect.bottom <= window.innerHeight &&
          rect.left >= 0 &&
          rect.right <= window.innerWidth
        );
        
        // If element is already visible, don't scroll
        if (isVisible) {
          return;
        }
      }
    }
    
    if (['finished', 'skipped'].includes(status)) {
      // Mark tour as seen when finished or skipped
      if (currentTourName) {
        markTourAsSeen(currentTourName);
      }
      setRun(false);
      setStepIndex(0);
      setCurrentTourName(null);
    } else if (type === 'step:after' && action === 'next') {
      setStepIndex(index + 1);
    } else if (type === 'step:after' && action === 'prev') {
      setStepIndex(Math.max(0, index - 1));
    }
  }, [currentTourName]);

  const value = useMemo(
    () => ({
      startDashboardTour,
      startIncomeManagementTour,
      startExpenseTrackingTour,
      startAssetPortfolioTour,
      startDebtLiabilitiesTour,
      startSetupTour,
      startMonteCarloTour,
      startInvestmentsTour,
      autoStartTour,
    }),
    [
      startDashboardTour,
      startIncomeManagementTour,
      startExpenseTrackingTour,
      startAssetPortfolioTour,
      startDebtLiabilitiesTour,
      startSetupTour,
      startMonteCarloTour,
      startInvestmentsTour,
      autoStartTour,
    ]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <Joyride
        run={run}
        steps={steps}
        stepIndex={stepIndex}
        continuous
        scrollToFirstStep
        showProgress
        showSkipButton
        disableOverlayClose
        scrollOffset={100}
        disableScrolling={false}
        disableScrollParentFix={true}
        floaterProps={{
          disableAnimation: false,
        }}
        styles={{
          options: {
            primaryColor: '#2563EB',
            zIndex: 2000,
          }
          ,
          tooltipContainer: {
            textAlign: 'left',
          },
          tooltip: {
            padding: '16px 18px',
            borderRadius: 12,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
            maxWidth: 380,
          },
          buttonNext: {
            backgroundColor: '#2563EB',
            borderRadius: 8,
          },
          buttonBack: {
            color: '#6B7280',
            marginRight: 8,
          }
        }}
        callback={handleCallback}
      />
    </TourContext.Provider>
  );
};


